import { supabase } from './supabase';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface AppConfiguration {
  id: string;
  key: string;
  value: any;
  description?: string;
  category: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

export interface AppAsset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'audio';
  file_path: string;
  file_size?: number;
  mime_type?: string;
  metadata?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  uploaded_by?: string;
}

export interface ThemeColors {
  primary: string;
  primaryContainer: string;
  onPrimary: string;
  onPrimaryContainer: string;
  lightBrown: string;
  white: string;
  softBlue: string;
  darkBrown: string;
  charcoal: string;
  sage: string;
  secondary: string;
  secondaryContainer: string;
  onSecondary: string;
  onSecondaryContainer: string;
  tertiary: string;
  tertiaryContainer: string;
  onTertiary: string;
  onTertiaryContainer: string;
  surface: string;
  surfaceVariant: string;
  onSurface: string;
  onSurfaceVariant: string;
  background: string;
  onBackground: string;
  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;
  outline: string;
  outlineVariant: string;
  accent: string;
  warning: string;
  success: string;
  info: string;
  worship: string;
  study: string;
  youth: string;
  community: string;
}

export interface QuickAction {
  title: string;
  icon: string;
  color: string;
  action: string;
  backgroundImage?: string;
  enabled: boolean;
}

export interface QuickActionsConfig {
  [key: string]: QuickAction;
}

export interface HeaderConfig {
  type: 'gradient' | 'image';
  gradient_colors?: string[];
  image_url?: string;
  enabled: boolean;
}

export interface HeaderImagesConfig {
  [key: string]: HeaderConfig;
}

export interface AppSettings {
  app_name: string;
  app_version: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  latest_ios_build: string;
  latest_android_build: string;
  ios_store_url: string;
  android_store_url: string;
  update_prompt_title: string;
  update_prompt_message: string;
  home_logo_url?: string;
  default_language: string;
  supported_languages: string[];
  max_file_upload_size: number;
  allowed_file_types: string[];
  push_notifications_enabled: boolean;
  analytics_enabled: boolean;
  debug_mode: boolean;
  /** Bulleted list shown on app home below Spotlight. Set by Super Admin. */
  app_roadmap?: string[];
  /** Configurable books displayed on the mobile Book Store screen. */
  book_store_books?: BookStoreBook[];
}

export interface BookStoreBook {
  id: string;
  title: string;
  excerpt: string;
  image_url: string;
  amazon_url: string;
}

const APP_SETTINGS_DEFAULTS: AppSettings = {
  app_name: 'NCOF Church App',
  app_version: '1.0.0',
  maintenance_mode: false,
  maintenance_message: 'App is under maintenance. Please check back later.',
  latest_ios_build: '1.0.0',
  latest_android_build: '1.0.0',
  ios_store_url: 'https://apps.apple.com/ca/app/new-church-of-faith/id6752209128',
  android_store_url: 'https://play.google.com/store/apps/details?id=com.alwinsley.newchurchoffaith',
  update_prompt_title: 'Update available',
  update_prompt_message: 'A newer version of the app is available. Please update for the best experience.',
  default_language: 'en',
  supported_languages: ['en', 'es', 'fr'],
  max_file_upload_size: 10_485_760,
  allowed_file_types: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'audio/mp3'],
  push_notifications_enabled: true,
  analytics_enabled: true,
  debug_mode: false,
};

export interface FeatureFlags {
  sermons_enabled: boolean;
  social_enabled: boolean;
  announcements_enabled: boolean;
  events_enabled: boolean;
  kids_church_enabled: boolean;
  volunteer_system_enabled: boolean;
  giving_system_enabled: boolean;
  businesses_enabled: boolean;
  service_planner_enabled: boolean;
  request_ride_enabled: boolean;
  store_enabled: boolean;
  classroom_enabled: boolean;
  confessions_enabled: boolean;
  devotionals_enabled: boolean;
  verification_system_enabled: boolean;
}

export interface ContentGuidelines {
  max_post_length: number;
  max_comment_length: number;
  profanity_filter_enabled: boolean;
  auto_moderation_enabled: boolean;
  require_approval_for_posts: boolean;
  require_approval_for_comments: boolean;
  allowed_media_types: string[];
  max_media_per_post: number;
  content_warning_required: boolean;
}

export interface NotificationSettings {
  default_categories: {
    [key: string]: boolean;
  };
  quiet_hours: {
    enabled: boolean;
    start_time: string;
    end_time: string;
  };
  batch_notifications: boolean;
  notification_sound_enabled: boolean;
  vibration_enabled: boolean;
}

const toNumericSegments = (value: unknown): number[] => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return [];

  return normalized
    .split(/[^0-9]+/)
    .filter(Boolean)
    .map((segment) => Number.parseInt(segment, 10))
    .filter((segment) => Number.isFinite(segment));
};

const shouldPreferAppVersion = (appVersion: unknown, platformVersion: unknown) => {
  const normalizedAppVersion = String(appVersion ?? '').trim();
  if (!normalizedAppVersion) return false;

  const normalizedPlatformVersion = String(platformVersion ?? '').trim();
  if (!normalizedPlatformVersion || normalizedPlatformVersion === '0') return true;

  const appVersionSegments = toNumericSegments(normalizedAppVersion);
  const platformVersionSegments = toNumericSegments(normalizedPlatformVersion);

  return appVersionSegments.length > 1 && platformVersionSegments.length <= 1;
};

const normalizeAppSettingsVersions = (settings: AppSettings): AppSettings => {
  const normalizedAppVersion = String(settings.app_version ?? '').trim();
  if (!normalizedAppVersion) return settings;

  return {
    ...settings,
    latest_ios_build: shouldPreferAppVersion(normalizedAppVersion, settings.latest_ios_build)
      ? normalizedAppVersion
      : String(settings.latest_ios_build ?? '').trim(),
    latest_android_build: shouldPreferAppVersion(normalizedAppVersion, settings.latest_android_build)
      ? normalizedAppVersion
      : String(settings.latest_android_build ?? '').trim(),
  };
};

export type PushNotificationRoleConfig = Record<string, string[]>;

class AppConfigService {
  async getAllConfigurations(): Promise<ApiResponse<AppConfiguration[]>> {
    try {
      const { data, error } = await supabase
        .from('app_configurations')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching configurations:', error);
        return { success: false, message: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception fetching configurations:', error);
      return { success: false, message: error.message };
    }
  }

  async getConfiguration<T>(key: string): Promise<ApiResponse<T>> {
    try {
      const { data, error } = await supabase.rpc('get_app_configuration', { config_key: key });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching configuration:', error);
        return { success: false, message: error.message };
      }

      return { success: true, data: data as T };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception fetching configuration:', error);
      return { success: false, message: error.message };
    }
  }

  async updateConfiguration(
    key: string,
    value: any,
    description?: string,
    category: string = 'general'
  ): Promise<ApiResponse<string>> {
    try {
      const { data, error } = await supabase.rpc('update_app_configuration', {
        config_key: key,
        config_value: value,
        config_description: description,
        config_category: category,
      });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating configuration:', error);
        return { success: false, message: error.message };
      }

      return { success: true, data: data as string };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception updating configuration:', error);
      return { success: false, message: error.message };
    }
  }

  async getThemeColors(): Promise<ApiResponse<ThemeColors>> {
    const result = await this.getConfiguration<ThemeColors>('theme_colors');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch theme colors' };
  }

  async updateThemeColors(colors: Partial<ThemeColors>): Promise<ApiResponse<string>> {
    const currentColors = await this.getThemeColors();
    if (!currentColors.success) {
      return { success: false, message: 'Failed to fetch current theme colors' };
    }

    const updatedColors = { ...currentColors.data, ...colors };
    return await this.updateConfiguration(
      'theme_colors',
      updatedColors,
      'Main theme colors for the app',
      'theme'
    );
  }

  async getQuickActions(): Promise<ApiResponse<QuickActionsConfig>> {
    const result = await this.getConfiguration<QuickActionsConfig>('quick_actions');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch quick actions' };
  }

  async updateQuickActions(actions: QuickActionsConfig): Promise<ApiResponse<string>> {
    return await this.updateConfiguration(
      'quick_actions',
      actions,
      'Quick actions configuration with icons, colors, and images',
      'quick_actions'
    );
  }

  async getHeaderImages(): Promise<ApiResponse<HeaderImagesConfig>> {
    const result = await this.getConfiguration<HeaderImagesConfig>('header_images');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch header images' };
  }

  async updateHeaderImages(headers: HeaderImagesConfig): Promise<ApiResponse<string>> {
    return await this.updateConfiguration(
      'header_images',
      headers,
      'Header configurations for different screens',
      'headers'
    );
  }

  async getAppSettings(): Promise<ApiResponse<AppSettings>> {
    const result = await this.getConfiguration<AppSettings>('app_settings');
    if (result.success && result.data) {
      return {
        success: true,
        data: normalizeAppSettingsVersions({ ...APP_SETTINGS_DEFAULTS, ...(result.data || {}) }),
      };
    }
    return { success: false, message: 'Failed to fetch app settings' };
  }

  async updateAppSettings(settings: Partial<AppSettings>): Promise<ApiResponse<string>> {
    const currentSettings = await this.getAppSettings();
    if (!currentSettings.success) {
      return { success: false, message: 'Failed to fetch current app settings' };
    }

    const updatedSettings = { ...currentSettings.data, ...settings };
    return await this.updateConfiguration(
      'app_settings',
      updatedSettings,
      'General app settings and configuration',
      'settings'
    );
  }

  async getFeatureFlags(): Promise<ApiResponse<FeatureFlags>> {
    const result = await this.getConfiguration<FeatureFlags>('feature_flags');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch feature flags' };
  }

  async updateFeatureFlags(flags: Partial<FeatureFlags>): Promise<ApiResponse<string>> {
    const currentFlags = await this.getFeatureFlags();
    if (!currentFlags.success) {
      return { success: false, message: 'Failed to fetch current feature flags' };
    }

    const updatedFlags = { ...currentFlags.data, ...flags };
    return await this.updateConfiguration(
      'feature_flags',
      updatedFlags,
      'Feature flags to enable/disable app features',
      'features'
    );
  }

  async getContentGuidelines(): Promise<ApiResponse<ContentGuidelines>> {
    const result = await this.getConfiguration<ContentGuidelines>('content_guidelines');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch content guidelines' };
  }

  async updateContentGuidelines(guidelines: Partial<ContentGuidelines>): Promise<ApiResponse<string>> {
    const currentGuidelines = await this.getContentGuidelines();
    if (!currentGuidelines.success) {
      return { success: false, message: 'Failed to fetch current content guidelines' };
    }

    const updatedGuidelines = { ...currentGuidelines.data, ...guidelines };
    return await this.updateConfiguration(
      'content_guidelines',
      updatedGuidelines,
      'Content moderation and posting guidelines',
      'content'
    );
  }

  async getNotificationSettings(): Promise<ApiResponse<NotificationSettings>> {
    const result = await this.getConfiguration<NotificationSettings>('notification_settings');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch notification settings' };
  }

  async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<ApiResponse<string>> {
    const currentSettings = await this.getNotificationSettings();
    if (!currentSettings.success) {
      return { success: false, message: 'Failed to fetch current notification settings' };
    }

    const updatedSettings = { ...currentSettings.data, ...settings };
    return await this.updateConfiguration(
      'notification_settings',
      updatedSettings,
      'Default notification settings for new users',
      'notifications'
    );
  }

  async getPushNotificationRoleConfig(): Promise<ApiResponse<PushNotificationRoleConfig>> {
    const result = await this.getConfiguration<PushNotificationRoleConfig>('push_notification_role_config');
    if (result.success && result.data) {
      return { success: true, data: result.data };
    }
    return { success: false, message: 'Failed to fetch push notification role config' };
  }

  async updatePushNotificationRoleConfig(
    config: PushNotificationRoleConfig
  ): Promise<ApiResponse<string>> {
    return await this.updateConfiguration(
      'push_notification_role_config',
      config,
      'Role targeting for push notification types',
      'notifications'
    );
  }

  async getAllAssets(): Promise<ApiResponse<AppAsset[]>> {
    try {
      const { data, error } = await supabase
        .from('app_assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching assets:', error);
        return { success: false, message: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception fetching assets:', error);
      return { success: false, message: error.message };
    }
  }

  async uploadAsset(
    name: string,
    type: 'image' | 'video' | 'document' | 'audio',
    file: File | Blob,
    metadata?: any
  ): Promise<ApiResponse<AppAsset>> {
    try {
      const timestamp = Date.now();
      const fileExtension = name.split('.').pop();
      const fileName = `${type}_${timestamp}.${fileExtension}`;
      const filePath = `app-assets/${type}/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('app-assets').upload(filePath, file);

      if (uploadError) {
        // eslint-disable-next-line no-console
        console.error('Error uploading file:', uploadError);
        return { success: false, message: uploadError.message };
      }

      const { data: assetData, error: dbError } = await supabase
        .from('app_assets')
        .insert({
          name,
          type,
          file_path: filePath,
          file_size: 'size' in file ? file.size : undefined,
          mime_type: 'type' in file ? file.type : undefined,
          metadata: metadata || {},
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (dbError) {
        // eslint-disable-next-line no-console
        console.error('Error saving asset record:', dbError);
        return { success: false, message: dbError.message };
      }

      return { success: true, data: assetData };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception uploading asset:', error);
      return { success: false, message: error.message };
    }
  }

  async deleteAsset(assetId: string): Promise<ApiResponse<boolean>> {
    try {
      const { data: asset, error: fetchError } = await supabase
        .from('app_assets')
        .select('file_path')
        .eq('id', assetId)
        .single();

      if (fetchError) {
        // eslint-disable-next-line no-console
        console.error('Error fetching asset:', fetchError);
        return { success: false, message: fetchError.message };
      }

      const { error: storageError } = await supabase.storage.from('app-assets').remove([asset.file_path]);

      if (storageError) {
        // eslint-disable-next-line no-console
        console.error('Error deleting from storage:', storageError);
        return { success: false, message: storageError.message };
      }

      const { error: dbError } = await supabase.from('app_assets').delete().eq('id', assetId);

      if (dbError) {
        // eslint-disable-next-line no-console
        console.error('Error deleting from database:', dbError);
        return { success: false, message: dbError.message };
      }

      return { success: true, data: true };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Exception deleting asset:', error);
      return { success: false, message: error.message };
    }
  }

  getAssetUrl(filePath: string): string {
    const { data } = supabase.storage.from('app-assets').getPublicUrl(filePath);
    return data.publicUrl;
  }
}

export const appConfigService = new AppConfigService();
